/*
  Was eine Zelle der Verfuegbarkeitsmatrix anzeigt — reine Ableitung aus dem
  Tagesobjekt, ohne React.

  Bewusst hier und nicht in index.jsx: es ist die Stelle, die bei jeder
  fachlichen Aenderung angefasst wird, und sie laesst sich so ohne Rendering
  pruefen (wie availabilityDrafts.js).
*/
import {
  IconBlock,
  IconHoliday,
  IconRemove,
  IconSuccess,
  IconTime,
} from "../../design-system/icons";

// Trennzeichen der Zeitspanne, so wie das Backend sie baut
// (availability_common.py: f"{start} - {end}").
const RANGE_SEPARATOR = " - ";

export const statusLabelWithTime = (label, day) => (
  day?.is_full_day || !day?.availability_time
    ? label
    : `${label}\n${day.availability_time}`
);

export const holidayLabel = (day, t) => (
  day?.holiday_name && day.holiday_name !== "Ferien"
    ? `${t("availability.status.holiday")}\n${day.holiday_name}`
    : t("availability.status.holiday")
);

/*
  Zeitfenster eines Tages. Mehrere Fenster kommen als Zeilen aus dem Backend
  ("08:00 - 12:00\n14:00 - 18:00") — deshalb Zeilen statt eines Wertes.
*/
export const availabilityWindows = (day) => String(day?.availability_time || "")
  .split("\n")
  .map((line) => line.trim())
  .filter(Boolean);

/*
  "Nicht den ganzen Tag verfuegbar" — unabhaengig davon, ob die Person das
  selbst erfasst hat oder es aus dem Wochenplan-Standard stammt. Genau diese
  Tage bekommen die orange Zelle; woher der Eintrag kommt, sagt weiterhin die
  gestrichelte Umrandung (is_default).
*/
export const isPartialAvailability = (day) => (
  day?.status === "availability"
  && !day?.is_full_day
  && availabilityWindows(day).length > 0
);

/*
  Beschriftung der Monatszelle: Beginn oben, Ende unten. Die Spalte ist 52px
  breit, "08:00 - 16:00" passt dort nicht in eine Zeile.
  Bei mehreren Fenstern steht das erste; auf die uebrigen zeigt der Zaehler,
  und der volle Text bleibt im Titel-Attribut der Zelle.
*/
export const monthTimeLabel = (day) => {
  const [first] = availabilityWindows(day);
  if (!first) return null;
  const [from, to] = first.split(RANGE_SEPARATOR).map((part) => part.trim());
  return to ? `${from}\n${to}` : from;
};

/*
  Die Zelle zeigt nur, was die Person selbst gemeldet hat: Verfuegbarkeit,
  Abwesenheit und Ferien. Absenzen aus dem Schichtplan bleiben aussen vor —
  was dort geplant ist, steht im Schichtplan.
*/
export const cellMeta = (day, t) => {
  if (day?.is_pending) {
    const label = day?.status === "holiday"
      ? holidayLabel(day, t)
      : statusLabelWithTime(t("availability.status.absent"), day);
    return { label, tone: "pending", icon: IconTime, striped: true };
  }
  if (day?.status === "holiday" || day?.isHoliday) {
    return { label: holidayLabel(day, t), tone: "holiday", icon: IconHoliday, striped: true };
  }
  if (day?.status === "absence") {
    return {
      label: statusLabelWithTime(t("availability.status.absent"), day),
      tone: "absence",
      icon: IconBlock,
      striped: true,
    };
  }
  if (day?.status === "availability") {
    const partial = isPartialAvailability(day);
    return {
      label: statusLabelWithTime(t("availability.status.available"), day),
      tone: partial ? "partial" : "availability",
      icon: IconSuccess,
      dashed: day.is_default,
      monthLabel: partial ? monthTimeLabel(day) : null,
      extraWindows: partial ? availabilityWindows(day).length - 1 : 0,
    };
  }
  return { label: "–", tone: "empty", icon: IconRemove };
};
